import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { createContext, ReactNode, useRef, useState } from "react";
import ProgressBar from "../components/ProgressBar";
import OrderSummary from "../components/OrderSummary";
import { WizardStep } from "../types";
import lockIcon from "assets/img/customer/lock.svg";

export type MobilePrimaryAction = {
  label: string;
  onClick: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
} | null;

export const OrderMobilePrimaryActionContext = createContext<{
  action: MobilePrimaryAction;
  setAction: (next: MobilePrimaryAction) => void;
} | null>(null);

export default function OrderLayout(props: {
  children: ReactNode;
  step: WizardStep;
  maxStep: WizardStep;
  onStepClick: (step: WizardStep) => void;
}) {
  const { children, step, maxStep, onStepClick } = props;

  const [mobilePrimaryAction, setMobilePrimaryAction] = useState<MobilePrimaryAction>(null);
  
  const ctxRef = useRef<{
    action: MobilePrimaryAction;
    setAction: (next: MobilePrimaryAction) => void;
  } | null>(null);
  
  if (!ctxRef.current) {
    ctxRef.current = {
      get action() {
        return mobilePrimaryAction;
      },
      setAction: setMobilePrimaryAction,
    };
  }
  
  const mobilePrimaryActionCtx = ctxRef.current;

  return (
    <OrderMobilePrimaryActionContext.Provider value={mobilePrimaryActionCtx}>
      <Box width="100%" flex="1" minH="100vh" display="flex" flexDirection="column">
        <Box display={{ base: "none", md: "flex" }}>
          <ProgressBar step={step} maxStep={maxStep} onStepClick={onStepClick} />
        </Box>

        <Box display={{ base: "flex", md: "none" }}>
          <ProgressBar step={step} maxStep={maxStep} onStepClick={onStepClick} showSteps={false} showProgress={false} showHelp />
        </Box>

        <Box display={{ base: "flex", md: "none" }}>
          <ProgressBar step={step} maxStep={maxStep} onStepClick={onStepClick} showHelp={false} />
        </Box>

        <Flex
          flex="1"
          width="100%"
          direction={{ base: "column", md: "row" }}
          alignItems={{ base: "flex-start", md: "stretch" }}
          columnGap={{ base: "0", md: "0" }}
          rowGap="24px"
        >
          <Box
            pt="32px"
            flex={{ base: "1", md: "0 0 53%" }}
            width={{ base: "100%", md: "60%" }}
            minW="0"
            bg="white"
            h="100%"
            px={{ base: "24px", md: "24px", xl: "48px" }}
          >
            {children}

            <Box mt={{base: "0",md:"24px"}} display={{ base: "block", md: "none" }}>
              <OrderSummary step={step} maxStep={maxStep} />
              {mobilePrimaryAction ? (
                <>
                <Button
                  type="button"
                  w="100%"
                  height="44px"
                  padding="13px 16px"
                  borderRadius="100px"
                  border={mobilePrimaryAction.isDisabled || mobilePrimaryAction.isLoading ? "1px solid #E9E9E9" : "1px solid #2F80ED"}
                  bgColor="#2F80ED"
                  color="#FFF"
                  fontSize="14px"
                  lineHeight="14px"
                  fontWeight="bold"
                  isLoading={mobilePrimaryAction.isLoading}
                  isDisabled={mobilePrimaryAction.isDisabled || mobilePrimaryAction.isLoading}
                  _disabled={{ backgroundColor: "#E9E9E9 !important", borderColor: "#E9E9E9" }}
                  _hover={
                    mobilePrimaryAction.isDisabled || mobilePrimaryAction.isLoading
                      ? undefined
                      : { bgColor: "#fff !important", color: "#2F80ED", border: "1px solid #2F80ED" }
                  }
                  onClick={() => {
                    mobilePrimaryAction.onClick();
                    setTimeout(() => {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }, 0);
                  }}
                  transition="all 0.2s"
                  mt="24px"
                >
                  {mobilePrimaryAction.label}
                </Button>
                <Flex mt="16px" align="center" gap="12px">
                  <Box as="img" src={lockIcon} w="36px" h="36px" />
                  <Text fontSize="14px" color="#505E70">Your files and data are 100% secure. All uploaded files are encrypted & confidential.</Text>
                </Flex>
                </>
              ) : null}
            </Box>
          </Box>
          <Box
            flex={{ base: "1", md: "0 0 47%" }}
            height="auto"
            bg="#F5F7FA"
            width={{ base: "100%", md: "40%" }}
            borderLeft="1px solid #E6ECF3"
            px={{ base: "24px", md: "24px", xl: "48px" }}
            py={{ base: "16px", md: "32px" }}
            display={{ md: "block", base: "none" }}
            flexShrink={0}
          >
            <OrderSummary step={step} maxStep={maxStep} />
          </Box>
        </Flex>
      </Box>
    </OrderMobilePrimaryActionContext.Provider>
  );
}
